/**
 * @fileoverview My Team query hooks.
 * @description Wrap GET /users/me/team (every page; the caller's self-inclusive team with each
 * member's assigned banks) and GET /users/me/team/summary (the team's KPI cards, which ignore the
 * table's filters) for the Sales Team page.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import { getAllMyTeam, getMyTeamSummary } from '@/features/users/services/users.service'

import type {
  ListUsersFilters,
  MyTeamSummary,
  TeamMember,
} from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches the caller's team matching the filters.
 * @param {ListUsersFilters} [filters] - Filters and sort.
 * @returns {import('@tanstack/react-query').UseQueryResult<TeamMember[], ApiError>} The query
 * result; the previous rows stay shown while new filters load.
 */
export function useMyTeam(filters: ListUsersFilters = {}) {
  return useQuery<TeamMember[], ApiError>({
    queryKey: [...USERS_QUERY_KEYS.myTeam, filters],
    queryFn: () => getAllMyTeam(filters),
    placeholderData: keepPreviousData,
  })
}

/**
 * @description Fetches the team's KPI cards.
 * @returns {import('@tanstack/react-query').UseQueryResult<MyTeamSummary, ApiError>} The query
 * result.
 */
export function useMyTeamSummary() {
  return useQuery<MyTeamSummary, ApiError>({
    queryKey: USERS_QUERY_KEYS.myTeamSummary,
    queryFn: getMyTeamSummary,
  })
}
